import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Autocomplete,
  Checkbox,
  Chip,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { DataGrid } from "@mui/x-data-grid";
import Swal from "sweetalert2";
import { getRequest, postRequest, putRequest, deleteRequest } from "Includes/api/apicall";
import { GET_URL, POST_URL, PUT_URL, DEL_URL } from "Includes/urls";
import { AddCircleOutlineOutlined } from "@mui/icons-material";
import { TEAM_STATUS_CHOICES } from "Includes/teamStatus";

function AssignList(props) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ name: "", group_leader: "", companies: [] });
  const [editId, setEditId] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [teamStatusFilter, setTeamStatusFilter] = useState("all");

  const otherGroupCompanyMap = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => {
      if (!editId || row.id !== editId) {
        const groupName = row.name || "another group";
        if (Array.isArray(row.companies)) {
          row.companies.forEach((id) => {
            if (id && typeof id === "object") {
              map.set(id.id, groupName);
            } else if (id) {
              map.set(id, groupName);
            }
          });
        }
        if (Array.isArray(row.companies_details)) {
          row.companies_details.forEach((c) => {
            if (c?.id) map.set(c.id, groupName);
          });
        }
      }
    });
    return map;
  }, [rows, editId]);

  const filteredCompanies = useMemo(() => {
    const selectedIds = new Set((formData.companies || []).map((c) => c.id));
    return companies.filter((c) => {
      if (selectedIds.has(c.id)) return true;
      if (teamStatusFilter === "all") return true;
      const compStatus = (c.status_by_team || "active").toLowerCase().replace(/\s+/g, "_");
      const targetStatus = teamStatusFilter.toLowerCase().replace(/\s+/g, "_");
      return compStatus === targetStatus;
    });
  }, [companies, teamStatusFilter, formData.companies]);

  const sortedCompanies = useMemo(() => {
    const selectedIds = new Set((formData.companies || []).map((c) => c.id));
    return [...filteredCompanies].sort((a, b) => {
      const aOther = otherGroupCompanyMap.has(a.id);
      const bOther = otherGroupCompanyMap.has(b.id);
      const aSelected = selectedIds.has(a.id);
      const bSelected = selectedIds.has(b.id);

      const aRank = aOther ? 2 : aSelected ? 1 : 0;
      const bRank = bOther ? 2 : bSelected ? 1 : 0;

      if (aRank !== bRank) return aRank - bRank;
      return (a.name || a.code || "").localeCompare(b.name || b.code || "");
    });
  }, [filteredCompanies, otherGroupCompanyMap, formData.companies]);

  const companyFilterOptions = useMemo(() => {
    return createFilterOptions({
      limit: 100,
      matchFrom: "any",
      stringify: (option) => `${option.name || ""} ${option.code || ""} ${option.status_by_team || ""}`,
    });
  }, []);

  const totalAssignedClients = useMemo(() => {
    const uniqueIds = new Set();
    rows.forEach((row) => {
      if (Array.isArray(row.companies)) {
        row.companies.forEach((c) => {
          if (c && typeof c === "object") uniqueIds.add(c.id);
          else if (c) uniqueIds.add(c);
        });
      }
      if (Array.isArray(row.companies_details)) {
        row.companies_details.forEach((c) => {
          if (c?.id) uniqueIds.add(c.id);
        });
      }
    });
    return uniqueIds.size;
  }, [rows]);

  useEffect(() => {
    fetchData();
    fetchUsers();
    fetchCompanies();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getRequest(GET_URL.client_groups.api, {}, props);
      if (res && res.data) {
        setRows(Array.isArray(res.data) ? res.data : (res.data.data || []));
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const fetchUsers = async () => {
    try {
      const res = await getRequest(GET_URL.user.api, { limit: 1000 }, props);
      if (res && res.data) {
        setUsers(Array.isArray(res.data) ? res.data : (res.data.data || []));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await getRequest(GET_URL.company.api, { limit: 10000 }, props);
      if (res && res.data) {
        setCompanies(Array.isArray(res.data) ? res.data : (res.data.data || []));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleOpen = (item = null) => {
    setTeamStatusFilter("all");
    if (item) {
      setEditId(item.id);
      setFormData({
        name: item.name || "",
        group_leader: item.group_leader || "",
        companies: item.companies_details || [],
      });
    } else {
      setEditId(null);
      setFormData({ name: "", group_leader: "", companies: [] });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditId(null);
    setTeamStatusFilter("all");
  };

  const handleViewOpen = (item) => {
    setViewData(item);
    setViewOpen(true);
  };

  const handleViewClose = () => {
    setViewOpen(false);
    setViewData(null);
  };

  const getCompanyCoordinatorName = (companyId) => {
    const company = companies.find((c) => c.id === companyId);
    return company?.coordinator_name || "Unassigned";
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      Swal.fire("Error", "Name is required", "error");
      return;
    }
    const conflicting = (formData.companies || []).filter((c) => otherGroupCompanyMap.has(c.id));
    if (conflicting.length > 0) {
      const names = conflicting.map((c) => `${c.name || c.code} (${otherGroupCompanyMap.get(c.id)})`).join(", ");
      Swal.fire("Error", `Client(s) already assigned to another group: ${names}`, "error");
      return;
    }
    const payload = {
      name: formData.name,
      group_leader: formData.group_leader || null,
      companies: formData.companies.map((c) => c.id),
    };

    setLoading(true);
    try {
      if (editId) {
        await putRequest(`${PUT_URL.client_groups.api}${editId}/`, payload, props);
        Swal.fire("Success", "Group updated successfully", "success");
      } else {
        await postRequest(POST_URL.client_groups.api, payload, props);
        Swal.fire("Success", "Group created successfully", "success");
      }
      handleClose();
      fetchData();
    } catch (e) {
      console.error(e);
      const errorMsg =
        e?.response?.data?.companies?.[0] ||
        e?.response?.data?.name?.[0] ||
        e?.response?.data?.Reason ||
        e?.response?.data?.detail ||
        "Something went wrong";
      Swal.fire("Error", errorMsg, "error");
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          await deleteRequest(`${DEL_URL.client_groups.api}${id}/`, {}, props);
          Swal.fire('Deleted!', 'Group has been deleted.', 'success');
          fetchData();
        } catch (e) {
          console.error(e);
          Swal.fire("Error", "Failed to delete group", "error");
        }
        setLoading(false);
      }
    });
  };

  const columns = [
    { field: "name", headerName: "Group Name", flex: 1 },
    { field: "group_leader_name", headerName: "Group Leader", flex: 1 },
    {
      field: "companies",
      headerName: "Clients",
      flex: 2,
      renderCell: (params) => {
        const count = params.row.companies_details?.length || 0;
        const names = params.row.companies_details?.map((c) => c.name).join(", ") || "-";
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, overflow: "hidden", width: "100%" }}>
            <Chip
              size="small"
              label={count}
              color={count > 0 ? "primary" : "default"}
              variant="outlined"
              sx={{ height: 20, fontSize: "0.75rem", fontWeight: 600, flexShrink: 0 }}
            />
            <Typography variant="body2" noWrap sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {names}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <Box>
          <Button size="small" onClick={() => handleViewOpen(params.row)}>View</Button>
          <Button size="small" onClick={() => handleOpen(params.row)}>Edit</Button>
          <Button size="small" color="error" onClick={() => handleDelete(params.row.id)}>Delete</Button>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h5" fontWeight="bold">Client Tracking - Supervisor Configuration</Typography>
          <Chip
            label={`Total Assigned Clients: ${totalAssignedClients}`}
            color="primary"
            size="small"
            sx={{ fontWeight: 600 }}
          />
        </Box>
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineOutlined />}
          onClick={() => handleOpen()}
        >
          Create Group
        </Button>
      </Box>
      <Paper sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          disableSelectionOnClick
          getRowId={(row) => row.id}
        />
      </Paper>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>{editId ? "Edit Group" : "Create Group"}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Group Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Group Leader</InputLabel>
            <Select
              value={formData.group_leader}
              onChange={(e) => setFormData({ ...formData, group_leader: e.target.value })}
              label="Group Leader"
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {users && users.length > 0 && users.map((u) => (
                <MenuItem key={u.id} value={u.id}>{u.username || u.email}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", mt: 2, mb: 1, flexWrap: "wrap" }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="team-status-filter-label">Filter by Team Status</InputLabel>
              <Select
                labelId="team-status-filter-label"
                value={teamStatusFilter}
                label="Filter by Team Status"
                onChange={(e) => setTeamStatusFilter(e.target.value)}
              >
                {TEAM_STATUS_CHOICES.map((choice) => (
                  <MenuItem key={choice.value} value={choice.value}>
                    {choice.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
              <Chip
                size="small"
                label={`Selected in this group: ${formData.companies.length}`}
                color="primary"
                variant="outlined"
              />
              <Chip
                size="small"
                label={`Total Assigned: ${otherGroupCompanyMap.size + formData.companies.length}`}
                color="info"
              />
              <Typography variant="body2" color="text.secondary">
                ({sortedCompanies.length} available under filter)
              </Typography>
            </Box>
          </Box>
          <Autocomplete
            multiple
            disableCloseOnSelect
            limitTags={4}
            filterOptions={companyFilterOptions}
            options={sortedCompanies}
            getOptionLabel={(option) => option.name || option.code || ""}
            getOptionDisabled={(option) => otherGroupCompanyMap.has(option.id)}
            value={formData.companies}
            onChange={(event, newValue) => {
              setFormData({ ...formData, companies: newValue });
            }}
            ListboxProps={{ style: { maxHeight: 320 } }}
            sx={{
              '& .MuiAutocomplete-inputRoot': {
                maxHeight: 140,
                overflowY: 'auto',
              },
            }}
            renderOption={(props, option, { selected }) => {
              const isOtherGroup = otherGroupCompanyMap.has(option.id);
              const groupName = otherGroupCompanyMap.get(option.id);
              const status = option.status_by_team || "Active";
              return (
                <li {...props} key={option.id}>
                  <Checkbox
                    size="small"
                    style={{ marginRight: 8 }}
                    checked={selected}
                    disabled={isOtherGroup}
                  />
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: selected ? 600 : 400 }}>
                      {option.name || option.code || ""}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexShrink: 0 }}>
                      <Chip
                        size="small"
                        label={status}
                        variant="outlined"
                        sx={{ fontSize: "0.7rem", height: 20 }}
                      />
                      {isOtherGroup && (
                        <Typography variant="caption" sx={{ color: "error.main", fontWeight: 500 }}>
                          Already in {groupName}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" label="Clients" margin="normal" />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={viewOpen} onClose={handleViewClose} fullWidth maxWidth="sm">
        <DialogTitle>Group Structure</DialogTitle>
        <DialogContent dividers>
          {viewData && (
            <Box>
              <Box
                sx={{
                  p: 2,
                  bgcolor: "#eff6ff",
                  borderRadius: 2,
                  border: "1px solid #bfdbfe",
                  mb: 3
                }}
              >
                <Typography variant="h6" fontWeight="bold" color="primary.dark">
                  Group Leader: {viewData.group_leader_name || "Unassigned"}
                </Typography>
              </Box>

              <Box pl={2}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: "text.secondary" }}>
                  Clients ({viewData.companies_details?.length || 0}):
                </Typography>
                {viewData.companies_details?.length > 0 ? (
                  viewData.companies_details.map((client, index) => (
                    <Paper
                      key={client.id}
                      elevation={0}
                      sx={{
                        mb: 2,
                        p: 2,
                        border: "1px solid #e5e7eb",
                        borderRadius: 2,
                        borderLeft: "4px solid #3b82f6"
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                        {index + 1}. {client.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Coordinator: {getCompanyCoordinatorName(client.id)}
                      </Typography>
                    </Paper>
                  ))
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ fontStyle: "italic" }}>
                    No clients assigned to this group.
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleViewClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AssignList;
